import React from 'react';
import { Box, FormControl, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import { useCountry } from '../contexts/CountryContext';

/**
 * Selector de país para la barra de navegación.
 *
 * - Si el usuario no tiene países, no muestra nada.
 * - Si tiene exactamente un país, muestra su nombre como etiqueta (sin desplegable), según el
 *   requerimiento del cliente ("si solo tengo un país que no me salga el desplegable").
 * - Si tiene varios, muestra un desplegable. Al cambiar de país se recarga la vista para que toda
 *   la información se vuelva a pedir acotada al nuevo país.
 */
const CountrySelector: React.FC = () => {
  const { countries, selectedCountry, selectCountry } = useCountry();

  if (!countries || countries.length === 0) {
    return null;
  }

  if (countries.length === 1) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
        <PublicIcon fontSize="small" sx={{ color: '#004F86' }} />
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#004F86' }}>
          {countries[0].name}
        </Typography>
      </Box>
    );
  }

  const handleChange = (event: SelectChangeEvent) => {
    const id = Number(event.target.value);
    const country = countries.find((c) => c.id === id) || null;
    selectCountry(country);
    // Se recarga para que todas las páginas vuelvan a cargar datos con el nuevo país.
    window.location.reload();
  };

  return (
    <FormControl size="small" sx={{ minWidth: 150 }}>
      <Select
        value={selectedCountry ? String(selectedCountry.id) : ''}
        onChange={handleChange}
        displayEmpty
        startAdornment={<PublicIcon fontSize="small" sx={{ color: '#004F86', mr: 0.5 }} />}
        sx={{
          fontWeight: 'bold',
          color: '#004F86',
          '.MuiOutlinedInput-notchedOutline': { borderColor: '#cfd8dc' },
        }}
        renderValue={(value) => {
          if (!value) return 'Seleccionar país';
          const country = countries.find((c) => String(c.id) === value);
          return country ? country.name : 'Seleccionar país';
        }}
      >
        {countries.map((country) => (
          <MenuItem key={country.id} value={String(country.id)}>
            <strong style={{ marginRight: 6 }}>{country.code}</strong> {country.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default CountrySelector;
